import { Download } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Button, Checkbox, Divider, FormControlLabel, Grid, MenuItem, Modal, Select, TextField, Toolbar, Typography } from '@mui/material';
import { Box, Container, Stack } from '@mui/system';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    startOfMonth,
    startOfQuarter,
    startOfToday,
    startOfWeek,
    startOfYear,
    subMonths,
    subQuarters,
    subWeeks,
    subYears,
} from 'date-fns';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { UpdateExportRecordPaymentApi } from '../../../../redux/Invoice/action';
import { saveFileFromResponse } from '../../../../utility/helpers/fileDownload';
import useRecordPaymentStore from '../../../../store/UseRecordPaymentStore';
import SaveAndSendSvg from '../../../../utility/Svg/SaveAndSendSvg';
import { toISOStringUTC, toISOStringUTCEnd } from '../../../../utility/helpers/commonFunction';

// Time period options configuration
const TIME_PERIODS = [
    { value: 'allTime', label: 'All Time' },
    { value: 'custom', label: 'Custom' },
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'thisQuarter', label: 'This Quarter' },
    { value: 'thisYear', label: 'This Year' },
    { divider: true },
    { value: 'lastWeek', label: 'Last Week' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'lastQuarter', label: 'Last Quarter' },
    { value: 'lastYear', label: 'Last Year' },
];

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    borderRadius: '8px 8px 0px 0px',
    boxShadow: 24,
};

const RecordPaymentSummaryModal = ({ open, close, id, data, selectedRows, setSelectedRows, getMultipleRecordPayments }) => {
    const dispatch = useDispatch();
    const { getSavePayload, showLoader, setShowLoader } = useRecordPaymentStore();

    // Initialize email selections
    const availableEmails = useMemo(() => {
        const emails = [];
        if (data?.email) emails.push({ type: 'Primary', email: data.email });
        if (data?.secondaryEmail) emails.push({ type: 'Secondary', email: data.secondaryEmail });
        return emails;
    }, [data]);

    const [selectedEmails, setSelectedEmails] = useState(availableEmails.map((item) => item.email));
    const [emailBody, setEmailBody] = useState('');
    const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // Generate placeholder text
    const placeholderText = useMemo(() => {
        const startDate = fromDate ? dayjs(fromDate).format('DD MMMM YYYY') : '{Start Date}';
        const endDate = toDate ? dayjs(toDate).format('DD MMMM YYYY') : '{End Date}';
        return `Please find your payment summary attached for invoices for period of ${startDate} to ${endDate}.`;
    }, [fromDate, toDate]);

    // Validation helper
    const validateDates = useCallback(() => {
        if (!fromDate || !toDate) {
            toast.error('Please enter from date and to date');
            return false;
        }
        if (dayjs(fromDate).format('YYYY-MM-DD') === 'Invalid Date') {
            toast.error('Please enter valid from date');
            return false;
        }
        if (dayjs(toDate).format('YYYY-MM-DD') === 'Invalid Date') {
            toast.error('Please enter valid to date');
            return false;
        }
        return true;
    }, [fromDate, toDate]);

    // Reset modal state
    const resetModal = useCallback(() => {
        setFromDate(null);
        setToDate(null);
        setSelectedTimePeriod('');
        setEmailBody('');
        setSelectedEmails(availableEmails.map((item) => item.email));
    }, [availableEmails]);

    const handleClose = useCallback(() => {
        close(false);
        resetModal();
    }, [close, resetModal]);

    // Handle time period selection
    const handleChangeTimePeriod = useCallback((event) => {
        const selectedType = event.target.value;
        setSelectedTimePeriod(selectedType);

        if (selectedType === 'custom') return;

        const today = startOfToday();
        let from = null;
        let to = null;

        const periodMap = {
            today: [today, today],
            thisWeek: [startOfWeek(today), endOfWeek(today)],
            thisMonth: [startOfMonth(today), endOfMonth(today)],
            thisQuarter: [startOfQuarter(today), endOfQuarter(today)],
            thisYear: [startOfYear(today), endOfYear(today)],
            lastWeek: [startOfWeek(subWeeks(today, 1)), endOfWeek(subWeeks(today, 1))],
            lastMonth: [startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))],
            lastQuarter: [startOfQuarter(subQuarters(today, 1)), endOfQuarter(subQuarters(today, 1))],
            lastYear: [startOfYear(subYears(today, 1)), endOfYear(subYears(today, 1))],
            allTime: [dayjs('01/01/2024').toDate(), today],
        };

        if (periodMap[selectedType]) {
            [from, to] = periodMap[selectedType];
            setFromDate(dayjs(from));
            setToDate(dayjs(to));
        }
    }, []);

    // Handle email selection
    const handleEmailChange = useCallback((email) => {
        setSelectedEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));
    }, []);

    // Handle API response
    const handleApiResponse = useCallback(
        (resp, type, successMessage) => {
            // on failure the response interceptor has already shown the server's message
            if (resp?.status === 200) {
                // Handle file download
                if (type !== 'email' && !saveFileFromResponse(resp, `payment-summary.${type}`)) return;

                // Success actions Close for now
                getMultipleRecordPayments(1, false);
                // setSelectedRows([]);
                // close(false);
                // resetModal();
                toast.success(successMessage);
            }
        },
        [getMultipleRecordPayments, setSelectedRows, close, resetModal]
    );

    // Handle download
    const handleDownload = useCallback(
        async (type) => {
            if (!validateDates()) return;

            const { isValid, payload } = getSavePayload();
            if (!isValid) return;

            setShowLoader(true);

            try {
                const body = {
                    fromDate: dayjs(fromDate).format('YYYY-MM-DD'),
                    toDate: dayjs(toDate).format('YYYY-MM-DD'),
                    format: type,
                    customerId: id,
                    invoices: payload,
                };

                const resp = await dispatch(UpdateExportRecordPaymentApi(body));
                handleApiResponse(resp, type, 'Payment summary exported successfully');
            } catch (error) {
                toast.error('Export failed');
            } finally {
                setShowLoader(false);
            }
        },
        [validateDates, getSavePayload, setShowLoader, fromDate, toDate, id, dispatch, handleApiResponse]
    );

    // Handle email send
    const handleSendEmail = useCallback(async () => {
        if (!validateDates()) return;
        if (selectedEmails.length === 0) {
            toast.error('Please select at least one email');
            return;
        }
        const { isValid, payload } = getSavePayload();
        if (!isValid) return;
        setShowLoader(true);

        try {
            const body = {
                fromDate: dayjs(fromDate).format('YYYY-MM-DD'),
                toDate: dayjs(toDate).format('YYYY-MM-DD'),
                emailTo: selectedEmails,
                emailBody: emailBody.trim() || placeholderText,
                format: 'email',
                customerId: id,
                invoices: payload,
            };

            const resp = await dispatch(UpdateExportRecordPaymentApi(body));
            handleApiResponse(resp, 'email', 'Payment summary was successfully emailed to the customer');
        } catch (error) {
            toast.error('Email sending failed');
        } finally {
            setShowLoader(false);
        }
    }, [validateDates, selectedEmails, setShowLoader, fromDate, toDate, emailBody, placeholderText, id, dispatch, handleApiResponse]);

    // Date constraints
    const shouldDisableDate = useCallback((date, compareDate, isAfter = false) => {
        if (!compareDate) return false;
        const targetTime = Date.parse(compareDate);
        const dateTime = Date.parse(date);
        return isAfter ? dateTime < targetTime : dateTime > targetTime;
    }, []);

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle} className="modalBodyBoxContainer">
                {/* Header */}
                <AppBar color="secondary" sx={{ height: 64, borderRadius: '8px 8px 0 0' }}>
                    <Toolbar>
                        <Typography variant="h6" sx={{ flexGrow: 1, color: 'white' }}>
                            Share Payment Summary
                        </Typography>
                        <CloseIcon sx={{ fill: 'white', fontSize: 32, cursor: 'pointer' }} onClick={handleClose} />
                    </Toolbar>
                </AppBar>

                {/* Content */}
                <Container disableGutters sx={{ mt: '64px' }}>
                    <Grid container sx={{ maxHeight: '78vh', overflowY: 'auto', p: 3 }}>
                        {/* Download Section */}
                        <Grid item xs={12} sx={{ mb: 3 }}>
                            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 3 }}>
                                <Download sx={{ color: '#006CDD', width: 28, height: 28 }} />
                                <Typography variant="h6">Download Report</Typography>
                            </Stack>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                        Select Time Period
                                    </Typography>
                                    <Select
                                        fullWidth
                                        value={selectedTimePeriod}
                                        onChange={handleChangeTimePeriod}
                                        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                                    >
                                        {TIME_PERIODS.map((period, index) =>
                                            period.divider ? (
                                                <Divider key={`divider-${index}`} />
                                            ) : (
                                                <MenuItem key={period.value} value={period.value}>
                                                    {period.label}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                        From
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            value={fromDate}
                                            onChange={(newValue) => {
                                                setFromDate(dayjs(newValue.format('YYYY-MM-DD')));
                                                setSelectedTimePeriod('custom');
                                            }}
                                            shouldDisableDate={(date) => shouldDisableDate(date, toDate)}
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                        To
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            value={toDate}
                                            onChange={(newValue) => {
                                                setToDate(dayjs(newValue.format('YYYY-MM-DD')));
                                                setSelectedTimePeriod('custom');
                                            }}
                                            shouldDisableDate={(date) => shouldDisableDate(date, fromDate, true)}
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>

                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button variant="contained" onClick={() => handleDownload('pdf')}>
                                    EXPORT PDF
                                </Button>
                                <Button variant="contained" onClick={() => handleDownload('csv')}>
                                    EXPORT CSV
                                </Button>
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        {/* Email Section */}
                        <Grid item xs={12}>
                            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                                <Box component="svg" width={28} height={28} viewBox="0 0 28 28" fill="none">
                                    <path
                                        d="M23.3333 4.66675C23.922 4.66656 24.489 4.88889 24.9206 5.28916C25.3523 5.68943 25.6167 6.23807 25.6608 6.82508L25.6667 7.00008V21.0001C25.6669 21.5888 25.4445 22.1557 25.0443 22.5874C24.644 23.019 24.0953 23.2834 23.5083 23.3276L23.3333 23.3334H4.66667C4.07799 23.3336 3.51101 23.1113 3.07936 22.711C2.64772 22.3107 2.38332 21.7621 2.33917 21.1751L2.33333 21.0001V19.8334H4.66667V21.0001H23.3333V8.64975L15.2367 16.7464C14.9304 17.0526 14.5215 17.2344 14.089 17.2566C13.6565 17.2788 13.2312 17.1398 12.8952 16.8666L12.7622 16.7464L4.66667 8.64975V9.33342H2.33333V7.00008C2.33315 6.41141 2.55547 5.84442 2.95575 5.41278C3.35602 4.98113 3.90465 4.71673 4.49167 4.67258L4.66667 4.66675H23.3333ZM7 15.1667C7.30942 15.1667 7.60617 15.2897 7.82496 15.5085C8.04375 15.7273 8.16667 16.024 8.16667 16.3334C8.16667 16.6428 8.04375 16.9396 7.82496 17.1584C7.60617 17.3772 7.30942 17.5001 7 17.5001H1.16667C0.857247 17.5001 0.560501 17.3772 0.341709 17.1584C0.122916 16.9396 0 16.6428 0 16.3334C0 16.024 0.122916 15.7273 0.341709 15.5085C0.560501 15.2897 0.857247 15.1667 1.16667 15.1667H7ZM21.6837 7.00008H6.31633L14 14.6837L21.6837 7.00008ZM5.83333 11.6667C6.13069 11.6671 6.41671 11.7809 6.63293 11.9851C6.84916 12.1892 6.97927 12.4682 6.9967 12.765C7.01413 13.0619 6.91755 13.3542 6.72669 13.5822C6.53584 13.8102 6.26511 13.9568 5.96983 13.9919L5.83333 14.0001H2.33333C2.03597 13.9998 1.74996 13.8859 1.53374 13.6818C1.31751 13.4776 1.18739 13.1986 1.16997 12.9018C1.15254 12.6049 1.24912 12.3126 1.43998 12.0846C1.63083 11.8566 1.90156 11.71 2.19683 11.6749L2.33333 11.6667H5.83333Z"
                                        fill="#006CDD"
                                    />
                                </Box>
                                <Typography variant="h6">Email Summary to Customer</Typography>
                            </Stack>

                            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                Select Email(s)
                            </Typography>
                            {availableEmails.map(({ type, email }) => (
                                <FormControlLabel
                                    key={email}
                                    control={<Checkbox checked={selectedEmails.includes(email)} onChange={() => handleEmailChange(email)} />}
                                    label={`${type} (${email})`}
                                />
                            ))}

                            <Typography variant="body1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
                                Add a message for {data?.company || `${data?.firstName} ${data?.lastName}`}
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={emailBody}
                                onChange={(e) => setEmailBody(e.target.value)}
                                placeholder={placeholderText}
                            />
                        </Grid>
                    </Grid>

                    {/* Footer */}
                    <Stack direction="row" justifyContent="flex-end" sx={{ p: 3 }}>
                        <Button variant="contained" startIcon={<SaveAndSendSvg />} size="large" onClick={handleSendEmail} disabled={showLoader}>
                            Email to Customer
                        </Button>
                    </Stack>
                </Container>
            </Box>
        </Modal>
    );
};

export default RecordPaymentSummaryModal;
